import { useEffect, useState } from 'react';
import {
  ImportColumnsResponseDto,
  ImportFileRequestDto,
  ImportFileResponseDto,
  ProductsTemplateCsvType,
} from '@tiendanube/common';
import { trackingCsvImportClick } from 'App/tracking';
import { useModal, useStorage } from 'commons/hooks';
import { useImportFile } from '../useImportFile';
import { useLoadCsvFileAndCompareColumns } from '../useLoadCsvFileAndCompareColumns';

type UpdateOptionsCatalogType = {
  updateStock: boolean;
  selectedLocation: string;
  template: ProductsTemplateCsvType;
};

type UpdateOptionsCustomersType = {
  acceptLegalRequirements: boolean;
};

export type UpdateOptionsType = UpdateOptionsCatalogType &
  UpdateOptionsCustomersType & {
    updateExisting: boolean;
  };

type ImportColumnsHandler = () => Promise<ImportColumnsResponseDto>;
type ImportFileHandler = (
  params: ImportFileRequestDto,
) => Promise<ImportFileResponseDto>;
type ImportJobKey = 'productsCsvImport' | 'customersCsvImport';

const PRODUCT_ID_COLUMN = 'Product Id';
const VARIANT_ID_COLUMN = 'Variant Id';

export function useImportCsv(
  importJobKey: ImportJobKey,
  importFileHandler: ImportFileHandler,
  importColumnsHandler: ImportColumnsHandler,
) {
  const [exportProductsTemplate] = useStorage<ProductsTemplateCsvType>(
    'export-products-template',
    'standard',
  );

  const defaultUpdateOptions: UpdateOptionsType = {
    updateExisting: true,
    updateStock: true,
    selectedLocation: 'all',
    acceptLegalRequirements: false,
    template: 'standard',
  };

  const [updateOptions, setUpdateOptions] =
    useState<UpdateOptionsType>(defaultUpdateOptions);
  const [
    showImportAlterationsModal,
    openShowImportAlterationsModal,
    closeShowImportAlterationsModal,
  ] = useModal();
  const [showExistingModal, openShowExistingModal, closeShowExistingModal] =
    useModal();
  const {
    onChangeFile,
    onRemoveFile,
    onRetry,
    status,
    fileName,
    badColumns,
    columnOptions,
    matchColumns,
    setMatchingColumns,
    ignoreAllColumns,
    undoIgnoreAllColumns,
    file,
    fileColumns,
    isLoading: isLoadingCsv,
    isError: isErrorCsv,
  } = useLoadCsvFileAndCompareColumns(importColumnsHandler);
  const {
    sendFile,
    isLoading: isLoadingImport,
    isError: isErrorImport,
    jobStatus,
    jobData,
    cleanJob,
  } = useImportFile(importJobKey, importFileHandler);

  const [showIncompatibleTemplateAlert, setShowIncompatibleTemplateAlert] =
    useState(false);

  const onUpdateOptionsChange = (prop: string, value: boolean | string) =>
    setUpdateOptions((options) => ({
      ...options,
      [prop]: value,
    }));

  const onCheckExisting = (): void => {
    if (!updateOptions.updateExisting) {
      openShowExistingModal();
    } else {
      onConfirm();
    }
  };

  const checkTemplateColumns = () => {
    let isCompatible = false;
    switch (updateOptions.template) {
      case 'metafield_products':
        if (
          fileColumns[0] === PRODUCT_ID_COLUMN &&
          fileColumns[1] !== VARIANT_ID_COLUMN
        )
          isCompatible = true;
        break;

      case 'metafield_variants':
        if (
          fileColumns[0] === PRODUCT_ID_COLUMN &&
          fileColumns[1] === VARIANT_ID_COLUMN
        )
          isCompatible = true;
        break;
    }
    setShowIncompatibleTemplateAlert(!isCompatible);
    trackingCsvImportClick(importJobKey, updateOptions.template, isCompatible);
    if (isCompatible) {
      onImport();
    }
  };

  const handleDismissIncompatibleTemplateAlert = () =>
    setShowIncompatibleTemplateAlert(false);

  const onConfirm = (): void => {
    closeShowExistingModal();
    if (updateOptions.template === 'standard') {
      openShowImportAlterationsModal();
    } else {
      checkTemplateColumns();
    }
  };

  const onImport = async () => {
    importJobKey === 'customersCsvImport' &&
      trackingCsvImportClick(importJobKey, updateOptions.template, true);

    closeShowImportAlterationsModal();
    const columns = fileColumns
      .map((key) =>
        ignoreColumn(matchColumns[key]) ? 'ignore' : matchColumns[key],
      )
      .join(',');
    const result = await sendFile({
      file,
      columns,
      replace: updateOptions.updateExisting,
      columnsChanged: !!badColumns.length,
      template: updateOptions.template,
    });
    if (result) {
      onRemoveFile();
      setUpdateOptions(defaultUpdateOptions);
    }
  };

  const ignoreColumn = (column: string) => {
    const { updateExisting, updateStock, selectedLocation } = updateOptions;
    if (updateExisting && column.match(/stock.*/)) {
      const ignoreLocation =
        selectedLocation !== 'all' && column !== `stock_${selectedLocation}`;
      return !updateStock || ignoreLocation;
    }
    return false;
  };

  useEffect(() => {
    if (importJobKey === 'productsCsvImport') {
      setUpdateOptions((options) => ({
        ...options,
        template: exportProductsTemplate,
      }));
    }
  }, [importJobKey, exportProductsTemplate]);

  return {
    updateOptions,
    showImportAlterationsModal,
    showExistingModal,
    jobStatus,
    jobData,
    cleanJob,
    onChangeFile,
    onRemoveFile,
    onRetry,
    status,
    fileName,
    badColumns,
    columnOptions,
    matchColumns,
    setMatchingColumns,
    ignoreAllColumns,
    undoIgnoreAllColumns,
    isLoadingCsv,
    isErrorCsv,
    isLoadingImport,
    isErrorImport,
    onUpdateOptionsChange,
    onCheckExisting,
    onConfirm,
    onImport,
    closeShowImportAlterationsModal,
    closeShowExistingModal,
    showIncompatibleTemplateAlert,
    handleDismissIncompatibleTemplateAlert,
  };
}
